/**
 * Contratos da visao geral: o que `GET /api/funnels/summary` e
 * `GET /api/engine/now` vao devolver.
 *
 * Decisoes nao obvias:
 *  1. Comissao, liquido, ticket medio e taxa chegam CALCULADOS pelo servidor,
 *     em centavos e bps inteiros. O navegador so exibe. A regra de
 *     arredondamento da comissao mora no backend (src/domain/money.ts);
 *     replica-la aqui criaria duas verdades sobre quanto a empresa paga
 *     (ADR-001).
 *  2. O PERIODO ANTERIOR viaja junto (`previous`), ja agregado pelo servidor
 *     sobre uma janela do mesmo tamanho imediatamente antes da atual. Fazer
 *     duas chamadas e comparar no navegador dobraria o custo da rota mais
 *     pesada do painel e ainda deixaria as duas janelas fora de sincronia
 *     quando o relogio virasse o dia entre uma e outra. `null` em "desde o
 *     inicio": nao existe "antes de tudo".
 *  3. `RecoveryChannel` amplia `CampaignChannel` (voz e WhatsApp) com os
 *     canais que o painel completo passa a atribuir: e-mail e vendedora. O
 *     motor ainda nao registra esses dois; o tipo nasce aqui para o anel "Por
 *     canal" e o backend nao divergirem depois.
 *  4. `LiveSnapshot` e um RETRATO DE AGORA, nao um agregado do periodo: quem
 *     esta na fila, quem esta em ligacao, quem respondeu e espera o comercial.
 *     Por isso vem de outra rota, com outro ritmo de atualizacao, e carrega
 *     `capturedAt` para todo "ha 4 min" ser calculado contra o relogio do
 *     servidor -- e nao contra o do navegador, que na previa e ficticio.
 *  5. Tudo aqui parte de um fato ja ocorrido: checkout abandonado ha 30 min ou
 *     assinatura vencida. Nao existe campo para "vai vencer" nem "lembrete
 *     antes de pagar": sem o fato nao ha recuperacao e, portanto, nao ha
 *     comissao (decisao do dono, 24/09/2026).
 */

import type { CompanyId, FunnelKind, Provider } from '../../types';

export type RecoveryChannel = 'voice' | 'whatsapp' | 'email' | 'seller';

/** As seis medidas de RETORNO que o lojista ve. Nunca "gasto" ou "custo". */
export interface ReturnMetrics {
  /** Valor pago pelos leads recuperados (ADR-001: valor pago, qualquer compra). */
  readonly recoveredAmountCents: number;
  /** Comissao do Converte Facil, congelada por conversao (`fee_bps_applied`). */
  readonly commissionCents: number;
  /** `recoveredAmountCents - commissionCents`: o que fica com a empresa. */
  readonly netAmountCents: number;
  readonly recoveredCount: number;
  /** Zero quando nao houve venda; a tela mostra o marcador de ausencia. */
  readonly averageTicketCents: number;
  /** Recuperados sobre os que entraram no funil, em bps (2112 = 21,12%). */
  readonly recoveryRateBps: number;
}

/** Janela fechada, ISO-8601, `start` inclusivo e `end` exclusivo. */
export interface PeriodBounds {
  readonly start: string;
  readonly end: string;
}

export interface FunnelSummary extends ReturnMetrics {
  readonly kind: FunnelKind;
  readonly active: boolean;
  /** Quantos entraram no funil na janela: abandonaram o checkout ou tiveram a assinatura vencida. */
  readonly enteredCount: number;
  /** Quantos ainda estao em regua (dentro dos 7 dias) ou ainda fora, sem desfecho. */
  readonly openCount: number;
  /** Valor dos checkouts/assinaturas de quem ainda esta em aberto: e o que ainda da para recuperar. */
  readonly openAmountCents: number;
  /** Quantos dos que entraram ja receberam ao menos um toque que chegou (ADR-001: contato). */
  readonly contactedCount: number;
  /** Membro da equipe responsavel pelo funil; `null` quando ninguem foi designado. */
  readonly assigneeId: string | null;
  /** As mesmas seis medidas na janela anterior de mesmo tamanho; `null` em "desde o inicio". */
  readonly previous: (ReturnMetrics & { readonly enteredCount: number; readonly openCount: number }) | null;
}

export interface ChannelSummary {
  readonly channel: RecoveryChannel;
  readonly recoveredAmountCents: number;
  readonly recoveredCount: number;
}

/** Uma oferta (produto ou plano) com o que entrou, voltou e ainda esta parado. */
export interface OfferSummary {
  readonly offerId: string;
  readonly name: string;
  readonly kind: FunnelKind;
  readonly provider: Provider;
  readonly priceCents: number;
  readonly enteredCount: number;
  readonly recoveredCount: number;
  readonly recoveredAmountCents: number;
  readonly openCount: number;
  readonly openAmountCents: number;
}

/**
 * Hora do dia (0..23, fuso da loja) em que as ligacoes foram mais atendidas na
 * janela. `null` quando houve menos de 10 ligacoes: um "melhor horario" com 3
 * ligacoes e ruido, nao insight.
 */
export interface BestHour {
  readonly hour: number;
  readonly calls: number;
  readonly answered: number;
}

/** Andamento do MES CORRENTE (calendario da loja), independente do filtro de periodo. */
export interface MonthProgress {
  /** `YYYY-MM` do mes corrente no fuso da loja. */
  readonly monthKey: string;
  /** Dias ja decorridos, incluindo hoje (1..daysInMonth). */
  readonly daysElapsed: number;
  readonly daysInMonth: number;
  readonly realizedAmountCents: number;
  readonly realizedNetCents: number;
  readonly realizedCount: number;
  /** Mes anterior fechado, para a projecao ter contra o que se medir. */
  readonly previousMonthAmountCents: number;
  readonly previousMonthNetCents: number;
  readonly previousMonthCount: number;
}

export interface FunnelsSummary {
  readonly companyId: CompanyId;
  readonly feeBps: number;
  readonly total: ReturnMetrics;
  /** Total na janela anterior de mesmo tamanho; `null` em "desde o inicio". */
  readonly previousTotal: ReturnMetrics | null;
  readonly previousPeriod: PeriodBounds | null;
  /** Um item por funil habilitado na empresa, na ordem de `CompanySummary.funnels`. */
  readonly funnels: readonly FunnelSummary[];
  /** Fatias do anel "Por canal"; somam exatamente `total.recoveredAmountCents`. */
  readonly channels: readonly ChannelSummary[];
  /** Uma linha por oferta ativa que teve entrada na janela; somam os funis. */
  readonly offers: readonly OfferSummary[];
  readonly bestHour: BestHour | null;
  readonly month: MonthProgress;
  readonly periodStart: string;
  readonly periodEnd: string;
}

/**
 * Retrato do motor neste instante. Cada numero e uma contagem de pessoas em um
 * estado AGORA; nenhum deles e soma do periodo.
 */
export interface LiveSnapshot {
  readonly companyId: CompanyId;
  /** Relogio do servidor no momento do retrato. Base de todo "ha X min". */
  readonly capturedAt: string;
  /** Abandonaram ha menos de 30 min: a janela ainda nao fechou, nenhum toque saiu. */
  readonly coolingCount: number;
  readonly coolingAmountCents: number;
  /** Ja passaram da janela e esperam o proximo toque da regua. */
  readonly queuedCount: number;
  readonly queuedAmountCents: number;
  /** Ligacoes da IA em curso neste instante. */
  readonly callsInProgress: number;
  /** Mensagens de WhatsApp enviadas nas ultimas 24 h sem resposta nem pagamento. */
  readonly awaitingReplyCount: number;
  /** Responderam e o comercial assumiu; ainda sem fecho. */
  readonly withSellerCount: number;
  /** Instante do proximo toque agendado; `null` com a fila vazia. */
  readonly nextTouchAt: string | null;
  /** Ultimo evento processado (webhook, ligacao, mensagem, pagamento). */
  readonly lastEventAt: string | null;
  readonly todayEnteredCount: number;
  readonly todayRecoveredCount: number;
  readonly todayRecoveredAmountCents: number;
  /** Leads em aberto com valor acima do limiar: os que mais valem uma acao humana. */
  readonly highValueOpen: {
    readonly count: number;
    readonly amountCents: number;
    readonly thresholdCents: number;
  };
}

export const CHANNEL_LABEL_FULL: Readonly<Record<RecoveryChannel, string>> = {
  voice: 'Ligação da IA',
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  seller: 'Vendedora',
};

/**
 * Slot de cor de cada canal no anel. Voz = serie 1 e WhatsApp = serie 3 sao a
 * convencao que ja existe no painel (StatChip, atividade recente, Contatos);
 * e-mail e vendedora ocupam as sobras 2 e 4 para nao ciclar a paleta.
 */
export const CHANNEL_TONE: Readonly<Record<RecoveryChannel, 1 | 2 | 3 | 4>> = {
  voice: 1,
  whatsapp: 3,
  email: 2,
  seller: 4,
};
